import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { debounceTime, distinctUntilChanged, merge, Observable, of, Subject, switchMap } from 'rxjs';
import { ClientPositionPickerState, ClientPositionPickerStore, ClientPositionVm } from './client-position-picker.store';
import { Point } from '../../models/point';
import { formatAmount, formatAmountInput, parseAmount } from '../../utils/format-amount';
import { ClickOutsideDirective } from '../../directives/click-outside.directive';

/**
 * What the zlecenie needs to add the position: the client-sourced CreateOrderItem
 * request, plus the name and number the page draws on the placeholder row.
 */
export interface ClientPositionSelected {
  clientOrderItemId: number;
  amount: number;
  name: string;
  unit: string;
  // the zamówienie the position belongs to — the order page links the new row to it right away
  clientOrderId: number;
  orderNumber: string;
  clientName: string;
}

/**
 * The order bank picker (0039): open positions of every zamówienie, each with an amount
 * input pre-filled with its remainder — the one place in the app where a quantity is
 * typed before a row exists, hence its own widget rather than a DropdownComponent.
 * Opens at a point like AddNodeDropdownComponent; owns its data like DropdownComponent.
 */
@Component({
  selector: 'client-position-picker',
  templateUrl: './client-position-picker.component.html',
  styleUrl: './client-position-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ClientPositionPickerStore],
  host: {
    '[style.left.px]': 'left',
    '[style.top.px]': 'top',
    '(clickOutside)': 'onClickOutside()'
  },
  hostDirectives: [
    {
      directive: ClickOutsideDirective,
      outputs: ['clickOutside']
    }
  ],
  imports: [AsyncPipe]
})
export class ClientPositionPickerComponent implements OnInit, OnDestroy {
  @Input({ required: true })
  set openAt(value: Point | undefined) {
    this.left = value?.x ?? 0;
    this.top = value?.y ?? 0;
  }

  @Output()
  readonly closed = new EventEmitter<void>();

  @Output()
  readonly positionSelected = new EventEmitter<ClientPositionSelected>();

  readonly inputElement = viewChild<ElementRef<HTMLInputElement>>('inputElement');

  left = 0;
  top = 0;

  readonly state$: Observable<ClientPositionPickerState>;

  formatAmount = formatAmount;
  formatAmountInput = formatAmountInput;

  private readonly searchTerm$ = new Subject<string>();

  constructor(
    private store: ClientPositionPickerStore,
    private destroyRef: DestroyRef
  ) {
    this.state$ = this.store.state$;
  }

  ngOnInit(): void {
    // Same rule as the list pages: the fetch waits for typing to settle, and switchMap
    // drops a superseded request so an older answer never overwrites a newer one.
    // The whole bank loads on open, undebounced — the list is the point, typing only narrows it
    merge(of(''), this.searchTerm$.pipe(debounceTime(300)))
      .pipe(
        distinctUntilChanged(),
        switchMap(term => this.store.fetch(term)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    this.inputElement()!.nativeElement.focus();
  }

  ngOnDestroy(): void {
    this.store.destroy();
  }

  onSearchChange(search: string): void {
    this.store.setSearch(search);
    this.searchTerm$.next(search);
  }

  // the whole label the row may have cut, then the term the label never shows
  positionTitle(position: ClientPositionVm): string {
    const label = `${position.name} · ${position.orderNumber} · ${position.clientName} · pozostało ${formatAmount(position.remaining, position.unit)}`;

    return position.deadline ? `${label} · Termin: ${position.deadline}` : label;
  }

  onAdd(position: ClientPositionVm, value: string): void {
    const amount = parseAmount(value);

    // the backend refuses more than the remainder; nothing to send for an amount it would reject
    if (!(amount > 0) || amount > position.remaining) {
      return;
    }

    const event: ClientPositionSelected = {
      clientOrderItemId: position.clientOrderItemId,
      amount,
      name: position.name,
      unit: position.unit,
      clientOrderId: position.clientOrderId,
      orderNumber: position.orderNumber,
      clientName: position.clientName
    };

    this.positionSelected.emit(event);

    this.close();
  }

  onClickOutside(): void {
    this.close();
  }

  private close(): void {
    this.closed.emit();
  }
}
